import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { type AppError, createAppError, request, requestText, siteLandingKeys } from '@/shared/api';

import type { EditableLanding, PublishedLanding } from '../model/types';
import {
  editableLandingSchema,
  landingPreviewSchema,
  publishedLandingSchema,
  toEditableLanding,
  toPublishedLanding,
} from './schema';

/**
 * The published landing, as anyone may read it — the booking app shows its
 * notice and the clinic's contact details from here.
 *
 * Content changes a few times a month, so it is cached for minutes rather than
 * seconds. A failure is not the booking app's problem: callers render nothing.
 *
 * Keyed by language: the server answers in the request's `Accept-Language`, so
 * a switch to English must not keep showing the Persian copy it cached.
 */
export function usePublishedLanding(): UseQueryResult<PublishedLanding, AppError> {
  const { i18n } = useTranslation();

  return useQuery<PublishedLanding, AppError>({
    queryKey: siteLandingKeys.publishedIn(i18n.language),
    queryFn: async ({ signal }): Promise<PublishedLanding> =>
      toPublishedLanding(
        await request('/site/landing', {
          schema: publishedLandingSchema,
          signal,
          auth: 'account',
          locale: i18n.language,
        }),
      ),
    staleTime: 5 * 60_000,
    retry: 1,
  });
}

/**
 * The editable document, its version and its images — `landing.manage` only.
 *
 * Not refetched behind the editor's back: the form keeps the version it was
 * read at, and a background refresh would only make the cache disagree with
 * what is on screen.
 */
export function useEditableLanding(
  options: { readonly enabled?: boolean } = {},
): UseQueryResult<EditableLanding, AppError> {
  return useQuery<EditableLanding, AppError>({
    queryKey: siteLandingKeys.editable(),
    queryFn: async ({ signal }): Promise<EditableLanding> =>
      toEditableLanding(
        await request('/staff/site/landing', { schema: editableLandingSchema, signal }),
      ),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    enabled: options.enabled ?? true,
  });
}

/**
 * The saved draft, rendered by the site's own template — an HTML document.
 *
 * Read through `requestText` rather than `request`: the body is not a JSON
 * envelope, and the staff bearer header is the reason it cannot be an iframe
 * `src`. Never cached across visits — a preview is only worth anything fresh.
 */
export function useLandingPreview(language: 'fa' | 'en' = 'fa'): UseQueryResult<string, AppError> {
  return useQuery<string, AppError>({
    queryKey: siteLandingKeys.previewIn(language),
    queryFn: async ({ signal }): Promise<string> => {
      const html = landingPreviewSchema.safeParse(
        await requestText(`/staff/site/landing/preview?language=${language}`, { signal }),
      );

      if (!html.success) {
        throw createAppError({ code: 'common.invalid_response', status: 200 });
      }

      return html.data;
    },
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  });
}
